import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { sendAdminAlert, sendNotaryRegistrationEmail } from '../lib/email'
import toast from 'react-hot-toast'

const ROLES = [
  { id: 'notary',     label: 'Notary Signing Agent',    desc: 'I want to accept signing jobs and get paid',       note: 'Requires approval & credential review' },
  { id: 'client',     label: 'Title Company / Lender',  desc: 'I need to order signings for my clients',          note: 'Business account — requires verification' },
  { id: 'individual', label: 'Individual / Borrower',   desc: 'I need a notary for personal documents',           note: 'Instant access — no approval needed' },
]

const STATES = ['Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia','Wisconsin','Wyoming']

const SIGNING_TYPES = ['Purchase / Sale', 'Refinance', 'HELOC', 'Reverse Mortgage', 'Loan Modification', 'Power of Attorney', 'Affidavit / Oath', 'Trust Signing', 'Commercial Closing', 'Seller Package', 'Witness Only', 'Other']

const LANGUAGES = ['English', 'Spanish', 'French', 'Portuguese', 'Mandarin', 'Cantonese', 'Vietnamese', 'Korean', 'Arabic', 'Haitian Creole', 'Russian', 'German', 'Italian', 'Japanese', 'Tagalog', 'Other']

const NSA_CERTIFICATIONS = ['National Notary Association (NNA)', 'Loan Signing System (LSS)', 'Notary2Pro', 'Notary.net', 'Notaries.com', 'Other']

const NOTARY_STEPS = ['Personal Info','Commission & Experience','Coverage & Equipment','Payment Info','Documents & Credentials','Review & Sign']

const STEP_ENCOURAGEMENT = [
  { emoji: '👋', msg: "Let's get started! Tell us about yourself." },
  { emoji: '🏆', msg: "Great start! Now let's verify your credentials." },
  { emoji: '🗺️', msg: "Halfway there!! Tell us your coverage area and equipment." },
  { emoji: '💳', msg: "Almost done!! Let's set up your payments." },
  { emoji: '📄', msg: "You're 83% there!! Just upload your documents." },
  { emoji: '✍️', msg: "Final step!! Review and sign your application." },
]

const DRAFT_KEY = 'lca_notary_registration_draft'

const EMPTY_FORM = {
  full_name: '', email: '', phone: '', password: '', confirm_password: '',
  sms_opt_in: false,
  physical_address: '', city: '', state_residence: '', zip: '',
  languages: [],
  notary_id: '', commission_number: '', commission_state: 'FL', commission_expires: '',
  eo_carrier: '', eo_expires: '', eo_coverage: '$100,000',
  years_experience: '', signings_completed: '',
  nna_certified: false, background_check: false, ron_certified: false, ron_platform: '',
  signing_types: [], nsa_certifications: [],
  service_areas: '', travel_radius: '25', availability: [],
  printer_type: '', scanner_type: '',
  payment_method: 'Direct Deposit',
  bank_name: '', routing_number: '', account_number: '',
  mailing_address_same: true, check_mailing_address: '',
  company_name: '',
  business_address: '', business_city: '', business_state: 'FL', business_zip: '',
}

// ── Reusable Multi-Select Dropdown ───────────────────────────
function MultiSelectDropdown({ label, options, selected, onChange, placeholder }) {
  const [open, setOpen] = React.useState(false)
  const ref = React.useRef(null)

  React.useEffect(() => {
    function handleClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  function toggle(opt) {
    if (opt === '__all__') {
      onChange(selected.length === options.length ? [] : [...options])
    } else {
      onChange(selected.includes(opt) ? selected.filter(s => s !== opt) : [...selected, opt])
    }
  }

  const allSelected = selected.length === options.length

  return (
    <div ref={ref} style={{ position:'relative' }}>
      <div onClick={() => setOpen(o => !o)} style={{ padding:'10px 14px', border:'1.5px solid #E0D9CC', borderRadius:10, background:'#fff', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:13, color: selected.length ? '#0D1B3E' : '#B8B5AB', minHeight:42 }}>
        <span style={{ flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {selected.length === 0 ? (placeholder || `Select ${label}...`) : selected.length === options.length ? `All ${label} selected` : selected.join(', ')}
        </span>
        <span style={{ color:'#B8B5AB', fontSize:11, marginLeft:8 }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div style={{ position:'absolute', top:'100%', left:0, right:0, background:'#fff', border:'1.5px solid #E0D9CC', borderRadius:10, zIndex:100, maxHeight:240, overflowY:'auto', boxShadow:'0 8px 24px rgba(0,0,0,0.1)', marginTop:4 }}>
          <div onClick={() => toggle('__all__')} style={{ padding:'10px 14px', cursor:'pointer', display:'flex', alignItems:'center', gap:10, borderBottom:'1px solid #F0EEE8', background: allSelected ? '#F8F6EF' : '#fff', fontSize:13, fontWeight:600, color:'#0D1B3E' }}>
            <span style={{ width:16, height:16, border:'2px solid', borderColor: allSelected ? '#C9A84C' : '#E0D9CC', borderRadius:4, background: allSelected ? '#C9A84C' : '#fff', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              {allSelected && <span style={{ color:'#fff', fontSize:10, fontWeight:700 }}>✓</span>}
            </span>
            Select All
          </div>
          {options.map(opt => {
            const isSelected = selected.includes(opt)
            return (
              <div key={opt} onClick={() => toggle(opt)} style={{ padding:'9px 14px', cursor:'pointer', display:'flex', alignItems:'center', gap:10, background: isSelected ? '#FFFDF5' : '#fff', fontSize:13, color:'#0D1B3E', borderBottom:'1px solid #F8F7F4' }}>
                <span style={{ width:16, height:16, border:'2px solid', borderColor: isSelected ? '#C9A84C' : '#E0D9CC', borderRadius:4, background: isSelected ? '#C9A84C' : '#fff', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  {isSelected && <span style={{ color:'#fff', fontSize:10, fontWeight:700 }}>✓</span>}
                </span>
                {opt}
              </div>
            )
          })}
        </div>
      )}
      {selected.length > 0 && (
        <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginTop:6 }}>
          {selected.map(s => (
            <span key={s} onClick={() => toggle(s)} style={{ background:'#FDF6E3', border:'1px solid rgba(201,168,76,0.3)', color:'#9A7A30', fontSize:11, padding:'2px 8px', borderRadius:20, cursor:'pointer', display:'flex', alignItems:'center', gap:4 }}>
              {s} <span style={{ fontWeight:700 }}>×</span>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Address Autocomplete using Nominatim (free, no API key) ──
function AddressAutocomplete({ value, onChange, onSelect, placeholder, required }) {
  const [suggestions, setSuggestions] = useState([])
  const [open,        setOpen]        = useState(false)
  const [loading,     setLoading]     = useState(false)
  const ref = React.useRef(null)
  const timer = React.useRef(null)

  React.useEffect(() => {
    function handleClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  async function lookup(query) {
    if (query.length < 4) { setSuggestions([]); setOpen(false); return }
    setLoading(true)
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&countrycodes=us&format=json&addressdetails=1&limit=5`, {
        headers: { 'Accept-Language': 'en' }
      })
      const data = await res.json()
      setSuggestions(data || [])
      setOpen(data?.length > 0)
    } catch {} finally { setLoading(false) }
  }

  function handleInput(e) {
    onChange(e.target.value)
    clearTimeout(timer.current)
    timer.current = setTimeout(() => lookup(e.target.value), 400)
  }

  function handleSelect(place) {
    const addr = place.address
    const street = [addr.house_number, addr.road].filter(Boolean).join(' ')
    const city   = addr.city || addr.town || addr.village || addr.county || ''
    const state  = addr.state || ''
    const zip    = addr.postcode || ''
    onChange(street || place.display_name.split(',')[0])
    onSelect({ street, city, state, zip })
    setSuggestions([])
    setOpen(false)
  }

  return (
    <div ref={ref} style={{ position:'relative' }}>
      <div style={{ position:'relative' }}>
        <input
          placeholder={placeholder || 'Start typing your address...'}
          value={value}
          onChange={handleInput}
          required={required}
          style={{ width:'100%' }}
          autoComplete="off"
        />
        {loading && <span style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', fontSize:11, color:'#C9A84C' }}>🔍</span>}
      </div>
      {open && suggestions.length > 0 && (
        <div style={{ position:'absolute', top:'100%', left:0, right:0, background:'#fff', border:'1.5px solid #E0D9CC', borderRadius:10, zIndex:200, maxHeight:220, overflowY:'auto', boxShadow:'0 8px 24px rgba(0,0,0,0.1)', marginTop:4 }}>
          {suggestions.map((s, i) => (
            <div key={i} onClick={() => handleSelect(s)} style={{ padding:'10px 14px', cursor:'pointer', fontSize:12, color:'#0D1B3E', borderBottom:'1px solid #F0EEE8', background:'#fff' }}
              onMouseEnter={e => e.currentTarget.style.background='#F8F6EF'}
              onMouseLeave={e => e.currentTarget.style.background='#fff'}>
              📍 {s.display_name}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default function RegisterPage() {
  const { signUp } = useAuth()
  const navigate   = useNavigate()
  const [params]   = useSearchParams()
  const [role,     setRole]     = useState(params.get('role') || '')
  const [step,     setStep]     = useState(role ? 2 : 1)
  const [notaryStep, setNotaryStep] = useState(0)
  const [loading,  setLoading]  = useState(false)
  const [countyLooking, setCountyLooking] = useState(false)

  async function lookupCounties(zip) {
    if (!zip || zip.length !== 5 || !/^\d{5}$/.test(zip)) return
    setCountyLooking(true)
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`)
      if (!res.ok) { toast.error('ZIP code not found!!'); return }
      const data    = await res.json()
      const lat     = parseFloat(data.places?.[0]?.latitude)
      const lng     = parseFloat(data.places?.[0]?.longitude)
      const state   = data.places?.[0]?.['state abbreviation'] || ''
      const city    = data.places?.[0]?.['place name'] || ''
      if (!lat || !lng) { toast.error('Could not locate ZIP code!!'); return }

      const degRadius = 0.36
      const bbox = `${lng - degRadius},${lat - degRadius},${lng + degRadius},${lat + degRadius}`
      const censusUrl = `https://tigerweb.geo.census.gov/arcgis/rest/services/TIGERweb/tigerWMS_Current/MapServer/84/query?where=1%3D1&geometry=${encodeURIComponent(bbox)}&geometryType=esriGeometryEnvelope&spatialRel=esriSpatialRelIntersects&outFields=BASENAME,STATE&returnGeometry=false&f=json&resultRecordCount=20`

      let counties = []
      try {
        const censusRes = await fetch(censusUrl)
        if (censusRes.ok) {
          const censusData = await censusRes.json()
          counties = censusData.features
            ?.map(f => f.attributes?.BASENAME)
            .filter(Boolean)
            .filter((v, i, a) => a.indexOf(v) === i)
            .sort() || []
        }
      } catch(e) {}

      if (!counties.length) {
        try {
          const osmUrl = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(city + ', ' + state)}&featuretype=county&format=json&addressdetails=1&limit=10&countrycodes=us`
          const osmRes = await fetch(osmUrl, { headers: { 'Accept-Language':'en' } })
          if (osmRes.ok) {
            const osmData = await osmRes.json()
            counties = osmData
              .map(r => r.address?.county?.replace(' County','') || r.display_name?.split(',')[0])
              .filter(Boolean)
              .filter((v, i, a) => a.indexOf(v) === i)
              .sort()
          }
        } catch(e) {}
      }

      if (counties.length > 0) {
        const areaText = counties.map(c => `${c} County, ${state}`).join(', ')
        set('service_areas', areaText)
        if (notaryStep >= 2) {
          toast.success(`✅ Found ${counties.length} ${counties.length === 1 ? 'county' : 'counties'} within 25 miles of ${zip}!!`, { duration:5000 })
        }
      } else {
        set('service_areas', `${city}, ${state}`)
        if (notaryStep >= 2) {
          toast(`📍 We found ${city}, ${state} — please add your service counties manually!!`, { duration:6000 })
        }
      }
    } catch (err) {
      console.error('County lookup failed:', err)
      toast.error('Could not auto-detect counties — please enter manually!!')
    } finally { setCountyLooking(false) }
  }

  const [error,    setError]    = useState('')
  const [digitalSignature, setDigitalSignature] = useState('')
  const [showPassword,     setShowPassword]     = useState(false)
  const [certChecked,      setCertChecked]      = useState(false)
  const [uploadedFiles,    setUploadedFiles]    = useState({})
  const [bgIssuedDate,     setBgIssuedDate]     = useState('')
  const [expires,          setExpires]          = useState({})
  const [hasDraft,         setHasDraft]         = useState(false)
  const [draftRestored,    setDraftRestored]    = useState(false)
  const [form, setForm] = useState(EMPTY_FORM)
  const [honeypot, setHoneypot] = useState('')          // 🪤 bots fill this; humans never see it
  const formLoadedAt = React.useRef(Date.now())          // 🪤 bots submit in seconds; humans don't

  useEffect(() => {
    try {
      const saved = localStorage.getItem(DRAFT_KEY)
      if (saved) {
        const draft = JSON.parse(saved)
        if (draft.email || draft.full_name) setHasDraft(true)
      }
    } catch (e) {}
  }, [])

  useEffect(() => {
    if (role !== 'notary') return
    try {
      const toSave = { ...form, password: '', confirm_password: '' }
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ ...toSave, _step: notaryStep }))
    } catch (e) {}
  }, [form, notaryStep, role])

  function restoreDraft() {
    try {
      const saved = localStorage.getItem(DRAFT_KEY)
      if (saved) {
        const draft = JSON.parse(saved)
        const { _step, ...formData } = draft
        setForm(f => ({ ...f, ...formData }))
        if (_step) setNotaryStep(_step)
        setDraftRestored(true)
        setHasDraft(false)
        setRole('notary')
        setStep(2)
      }
    } catch (e) {}
  }

  function clearDraft() {
    try { localStorage.removeItem(DRAFT_KEY) } catch (e) {}
    setHasDraft(false)
  }

  function set(k, v) { setForm(f => ({ ...f, [k]: v })) }
  function toggleArr(key, val) {
    setForm(f => ({ ...f, [key]: f[key].includes(val) ? f[key].filter(x => x !== val) : [...f[key], val] }))
  }

  const STEP4_DOCS = [
    { key:'gov_id_front',        docType:'government_id',    hasIssued:true,  hasExpiry:true,  label:'Government ID — Front' },
    { key:'gov_id_back',         docType:'government_id',    hasIssued:false, hasExpiry:false, label:'Government ID — Back' },
    { key:'commission_cert',     docType:'commission',       hasIssued:false, hasExpiry:true,  label:'Notary Commission Certificate' },
    { key:'eo_insurance',        docType:'eo_insurance',     hasIssued:false, hasExpiry:true,  label:'E&O Insurance Declaration Page' },
    { key:'w9_form',             docType:'w9_form',          hasIssued:false, hasExpiry:false, label:'W-9 Form' },
    { key:'background_check_doc',docType:'background_check', hasIssued:true,  hasExpiry:false, label:'Background Check' },
    { key:'nna_cert',            docType:'nna_cert',         hasIssued:false, hasExpiry:false, label:'Notary Signing Agent Certifications' },
    { key:'ron_cert',            docType:'ron_cert',         hasIssued:false, hasExpiry:true,  label:'RON Certification' },
  ]

  async function handleFile(key, file, label) {
    if (!file) return
    const docMeta = STEP4_DOCS.find(d => d.key === key)
    const isBgCheck = key === 'background_check_doc'
    if (docMeta) {
      if (docMeta.hasExpiry && !expires[key]) {
        toast.error(`📅 Enter the Expiration Date for "${label}" first — then upload!!`, { duration: 6000, icon: '⚠️' })
        return
      }
      if (docMeta.hasIssued) {
        const issuedVal = isBgCheck ? bgIssuedDate : expires[key + '_issued']
        if (!issuedVal) {
          toast.error(`📅 Enter the Issued Date for "${label}" first — then upload!!`, { duration: 6000, icon: '⚠️' })
          return
        }
      }
    }
    setUploadedFiles(f => ({ ...f, [key]: { name: file.name, uploading: true } }))
    try {
      const { data: { user: currentUser } } = await import('../lib/supabase').then(m => m.supabase.auth.getUser())
      if (!currentUser) {
        setUploadedFiles(f => ({ ...f, [key]: { name: file.name, file, pending: true } }))
        return
      }
      const ext  = file.name.split('.').pop()
      const path = `${currentUser.id}/${key}-${Date.now()}.${ext}`
      const { supabase } = await import('../lib/supabase')
      const { error: uploadErr } = await supabase.storage.from('credentials').upload(path, file)
      if (uploadErr) throw uploadErr
      const { data: { publicUrl } } = supabase.storage.from('credentials').getPublicUrl(path)
      await supabase.from('notary_docs').insert({
        notary_id:   currentUser.id,
        doc_type:    docMeta?.docType || key,
        doc_label:   label,
        file_url:    publicUrl,
        expires_at:  expires[key]           || null,
        issued_date: isBgCheck ? bgIssuedDate : (expires[key + '_issued'] || null),
        status:      'pending',
      })
      setUploadedFiles(f => ({ ...f, [key]: { name: file.name, uploaded: true, url: publicUrl } }))
    } catch (err) {
      setUploadedFiles(f => ({ ...f, [key]: { name: file.name, file, pending: true } }))
    }
  }

  async function uploadPendingFiles(userId) {
    const { supabase } = await import('../lib/supabase')
    for (const [key, fileData] of Object.entries(uploadedFiles)) {
      if (!fileData?.file) continue
      const docMeta   = STEP4_DOCS.find(d => d.key === key)
      const isBgCheck = key === 'background_check_doc'
      try {
        const ext  = fileData.file.name.split('.').pop()
        const path = `${userId}/${key}-${Date.now()}.${ext}`
        const { error: uploadErr } = await supabase.storage.from('credentials').upload(path, fileData.file)
        if (uploadErr) { console.error('Upload error:', key, uploadErr); continue }
        const { data: { publicUrl } } = supabase.storage.from('credentials').getPublicUrl(path)
        const labelMap = {
          gov_id_front:         'Government ID — Front',
          gov_id_back:          'Government ID — Back',
          commission_cert:      'Notary Commission Certificate',
          eo_insurance:         'E&O Insurance Declaration Page',
          w9_form:              'W-9 Form',
          background_check_doc: 'Background Check',
          nna_cert:             'Notary Signing Agent Certifications',
          ron_cert:             'RON Certification',
        }
        await supabase.from('notary_docs').insert({
          notary_id:   userId,
          doc_type:    docMeta?.docType || key,
          doc_label:   labelMap[key]    || key,
          file_url:    publicUrl,
          expires_at:  expires[key]     || null,
          issued_date: isBgCheck ? bgIssuedDate : (expires[key + '_issued'] || null),
          status:      'pending',
        })
      } catch (err) { console.error('uploadPendingFiles error:', key, err) }
    }
  }

  const inp = (label, key, type = 'text', placeholder = '', req = false) => {
    const id = `field-${key}`
    return (
      <div className="form-group">
        <label className="form-label" htmlFor={id}>
          {label}
          {req && <span style={{ color:'#E24B4A' }} aria-hidden="true"> *</span>}
          {req && <span className="sr-only"> (required)</span>}
        </label>
        <input id={id} type={type} placeholder={placeholder} value={form[key]} onChange={e => set(key, e.target.value)} required={req} aria-required={req ? 'true' : undefined} />
      </div>
    )
  }

  const fileInp = (label, key, required = false) => {
    const id       = `file-${key}`
    const fileData = uploadedFiles[key]
    const isUploaded  = fileData?.uploaded
    const isUploading = fileData?.uploading
    const isPending   = fileData?.pending
    const fileName    = fileData?.name
    return (
      <div style={{ marginBottom:10 }}>
        <label htmlFor={id} style={{ fontSize:12, fontWeight:500, color:'#7A7870', display:'block', marginBottom:5 }}>
          {label}
          {required && <span style={{ color:'#E24B4A' }} aria-hidden="true"> *</span>}
        </label>
        <label htmlFor={id} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', border:`1.5px dashed ${isUploaded||isPending?'#2D7A4F':'#E8E6DF'}`, borderRadius:10, cursor: isUploading ? 'wait' : 'pointer', fontSize:13, background:isUploaded||isPending?'#EAF5EE':'#F8F7F4', color:isUploaded||isPending?'#2D7A4F':'#7A7870' }}>
          <input id={id} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display:'none' }} onChange={e => handleFile(key, e.target.files[0], label)} capture="environment" disabled={isUploading} />
          <span aria-hidden="true" style={{ fontSize:18 }}>
            {isUploading ? '⏳' : isUploaded || isPending ? '✓' : '+'}
          </span>
          <span>
            {isUploading ? 'Uploading...' :
             isUploaded  ? `✓ ${fileName} — saved to your application!!` :
             isPending   ? `✓ ${fileName} — will upload on submit` :
             `Upload ${label}`}
          </span>
        </label>
      </div>
    )
  }

  const chip = (key, val) => {
    const id = `chip-${key}-${val.replace(/\s+/g,'-').toLowerCase()}`
    return (
      <label key={val} htmlFor={id} style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, cursor:'pointer', padding:'4px 12px', borderRadius:20, border:`1px solid ${form[key].includes(val)?'#C9A84C':'#E8E6DF'}`, background:form[key].includes(val)?'#FDF6E3':'#fff', transition:'all 0.12s' }}>
        <input id={id} type="checkbox" style={{ width:'auto' }} checked={form[key].includes(val)} onChange={() => toggleArr(key, val)} />
        {val}
      </label>
    )
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    // 🪤 BOT TRAPS: the invisible honeypot field + a submission speed check.
    // Humans never see the honeypot and never complete this form in under 4
    // seconds. Bots do both — so we pretend to succeed and save nothing,
    // teaching them nothing about how they were caught.
    if (honeypot || (Date.now() - formLoadedAt.current) < 4000) {
      setLoading(true)
      setTimeout(() => navigate('/pending'), 900)
      return
    }
    if (role === 'notary') {
      if (!certChecked) { setError('Please check the certification statement to continue'); return }
      if (!form.agreed_ica) { setError('Please agree to the Independent Contractor Agreement to continue'); return }
      if (!form.agreed_nppi) { setError('Please agree to the Confidentiality & Data Protection Agreement to continue'); return }
      if (!form.agreed_nonsolicitation) { setError('Please agree to the Non-Solicitation Agreement to continue'); return }
      if (!digitalSignature || digitalSignature.toLowerCase().trim() !== form.full_name.toLowerCase().trim()) {
        setError('Digital signature must match your full legal name exactly'); return
      }
    }
    if (form.password !== form.confirm_password) { setError('Passwords do not match'); return }
    if (form.password.length < 8) { setError('Password must be at least 8 characters'); return }
    setLoading(true)
    try {
      const result = await signUp(form.email, form.password, {
        full_name:           form.full_name,
        phone:               form.phone,
        role,
        company_name:        form.company_name,
        address:             form.physical_address || null,
        city:                form.city             || null,
        state:               form.state_residence  || null,
        zip:                 form.zip              || null,
        business_address:    form.business_address || null,
        business_city:       form.business_city    || null,
        business_state:      form.business_state   || null,
        business_zip:        form.business_zip     || null,
        printer_type:        form.printer_type,
        scanner_type:        form.scanner_type,
        signed_at:           role === 'notary' ? new Date().toISOString() : null,
        digital_signature:   role === 'notary' ? digitalSignature : null,
      })
      const userId = result?.user?.id
      if (userId) {
        await new Promise(resolve => setTimeout(resolve, 1500))
        await uploadPendingFiles(userId)
        // 📱 SMS consent record — who opted in and exactly when (carrier compliance proof)
        try {
          const { supabase: sbProfile } = await import('../lib/supabase')
          await sbProfile.from('profiles').update({
            sms_opt_in:    !!form.sms_opt_in,
            sms_opt_in_at: form.sms_opt_in ? new Date().toISOString() : null,
          }).eq('id', userId)
        } catch (smsErr) { console.error('SMS consent save error:', smsErr) }
        if (role === 'notary') {
          const { supabase: sb } = await import('../lib/supabase')
          const { error: notaryErr } = await sb.from('notaries').update({
            commission_number:  form.commission_number  || null,
            commission_state:   form.commission_state   || null,
            commission_expires: form.commission_expires || null,
            eo_carrier:         form.eo_carrier         || null,
            eo_coverage:        form.eo_coverage        || null,
            eo_expires:         form.eo_expires         || null,
            service_areas:      form.service_areas      || null,
            years_experience:   form.years_experience   ? parseInt(form.years_experience) : null,
            ron_certified:      form.ron_certified       || false,
            nsa_certifications: form.nsa_certifications || [],
            signing_types:      form.signing_types       || [],
            languages:          form.languages           || [],
            travel_radius:      form.travel_radius       || '25',
            availability:       form.availability        || [],
            printer_type:       form.printer_type        || null,
            scanner_type:       form.scanner_type        || null,
            background_check:   form.background_check    || false,
            signed_at:          new Date().toISOString(),
            digital_signature:  digitalSignature        || null,
            ica_agreed_at:      form.agreed_ica           ? new Date().toISOString() : null,
            nppi_agreed_at:     form.agreed_nppi          ? new Date().toISOString() : null,
            nonsolicitation_agreed_at: form.agreed_nonsolicitation ? new Date().toISOString() : null,
          }).eq('id', userId)
          if (notaryErr) console.error('Notary data save error:', notaryErr)
          else console.log('✅ Notary data saved successfully!!')
        }
      }

      await sendAdminAlert({
        client_name:   form.full_name,
        client_phone:  form.phone || '—',
        client_email:  form.email,
        service_name:  role === 'notary' ? 'New Notary Registration' : 'New Client Registration',
        amount:        0,
        address:       '—',
        appointment:   null,
        is_veteran:    false,
        special_notes: `New ${role} registered — please log in to review and approve!!`,
        portal_link:   role === 'client' ? 'https://loanclosingagent.com/admin/clients' : 'https://loanclosingagent.com/admin/notaries',
      })

      if (role === 'notary') {
        toast.success(`🎉 Application submitted successfully!! Check your email for next steps!!`, { duration: 5000 })
        sendNotaryRegistrationEmail({
          notary_name:  form.full_name,
          notary_email: form.email,
        }).catch(err => console.error('Notary registration email failed:', err))
      }

      clearDraft()
      await new Promise(resolve => setTimeout(resolve, 1500))
      if (role === 'individual') navigate('/order')
      else if (role === 'client') navigate('/pending')
      else navigate('/pending?registered=true')
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.')
    } finally { setLoading(false) }
  }

  const pct = role === 'notary' ? Math.round((notaryStep / (NOTARY_STEPS.length - 1)) * 100) : 100

  const styles = `
    .sr-only { position:absolute; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip:rect(0,0,0,0); white-space:nowrap; border:0; }
    :focus-visible { outline:2px solid #C9A84C; outline-offset:3px; border-radius:4px; }
    .lca-skip-link { position:absolute; top:-100px; left:16px; background:#C9A84C; color:#0D1B3E; padding:8px 16px; border-radius:8px; font-size:13px; font-weight:600; text-decoration:none; z-index:9999; transition:top .2s; }
    .lca-skip-link:focus { top:16px; }
    .lca-role-card { padding:16px 18px; border-radius:12px; cursor:pointer; transition:all 0.15s; text-align:left; width:100%; background:#fff; border:2px solid #E8E6DF; }
    .lca-role-card:hover { border-color:#C9A84C; background:#FDF6E3; }
    .lca-role-card[aria-pressed="true"] { border-color:#C9A84C; background:#FDF6E3; }
    .lca-back-btn { background:none; border:none; color:#9A7A30; cursor:pointer; font-size:13px; padding:4px 0; font-weight:500; }
    .lca-back-btn:hover { text-decoration:underline; }
    .lca-error { background:#FDF2F2; border:1px solid #F5C6C6; border-radius:10px; padding:10px 14px; font-size:13px; color:#C0392B; margin-bottom:16px; display:flex; align-items:flex-start; gap:8px; }
    .lca-progress-label { display:flex; justify-content:space-between; margin-bottom:6px; }
    .lca-nav-btns { display:flex; gap:10px; margin-top:24px; }
    .lca-checkbox-label { display:flex; align-items:center; gap:8px; font-size:13px; cursor:pointer; }
    .lca-checkbox-label input[type="checkbox"] { width:auto; }
    .lca-sig-ok  { font-size:11px; color:#2D7A4F; margin-top:4px; }
    .lca-sig-err { font-size:11px; color:#E24B4A; margin-top:4px; }
    .lca-required-note { font-size:11px; color:#B0A898; margin-bottom:12px; margin-top:-8px; }
    .lca-encouragement { background:linear-gradient(135deg,#0D1B3E,#1A2F5E); border-radius:12px; padding:14px 18px; margin-bottom:20px; display:flex; align-items:center; gap:12px; }
    .lca-encouragement-emoji { font-size:28px; flex-shrink:0; }
    .lca-encouragement-text { font-size:13px; color:#C9A84C; font-weight:500; }
    .lca-encouragement-sub { font-size:11px; color:rgba(255,255,255,0.5); margin-top:2px; }
    .lca-draft-banner { background:#FDF6E3; border:1px solid rgba(201,168,76,0.4); border-radius:12px; padding:14px 18px; margin-bottom:20px; display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap; }
    .lca-draft-restored { background:#EAF5EE; border:1px solid rgba(45,122,79,0.3); border-radius:12px; padding:10px 16px; margin-bottom:16px; font-size:12px; color:#2D7A4F; display:flex; align-items:center; gap:8px; }
  `

  return (
    <div style={{ minHeight:'100vh', background:'#0D1B3E', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'flex-start', padding:'24px 16px' }}>
      <style>{styles}</style>
      <a href="#register-main" className="lca-skip-link">Skip to registration form</a>

      {/* LOGO */}
      <Link to="/" style={{ textDecoration:'none', marginBottom:24, display:'inline-flex', justifyContent:'center' }} aria-label="Loan Closing Agent LLC — Go to homepage">
        <img src="/lca_logo_quill_map.png" alt="Loan Closing Agent LLC — Nationwide Signings and Notary Group" style={{ height:80, width:'auto', objectFit:'contain', filter:'drop-shadow(0 4px 16px rgba(201,168,76,0.18))' }} onError={e => { e.target.style.display='none' }} />
      </Link>

      {/* ✅ DRAFT RESUME BANNER */}
      {hasDraft && step === 1 && (
        <div className="lca-draft-banner" role="alert">
          <div>
            <div style={{ fontSize:13, fontWeight:600, color:'#8A5C00' }}>📝 You have a saved application!</div>
            <div style={{ fontSize:12, color:'#B8A060', marginTop:2 }}>Pick up right where you left off — your progress was saved automatically.</div>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            {/* ✅ CTA PASS — gold gradient resume button */}
            <button
              onClick={restoreDraft}
              type="button"
              style={{ background:'linear-gradient(135deg,#8A5C00 0%,#C9A84C 50%,#B8941F 100%)', color:'#fff', border:'none', padding:'9px 18px', borderRadius:10, fontSize:13, fontWeight:600, cursor:'pointer', letterSpacing:'0.04em', boxShadow:'0 4px 12px rgba(13,27,62,0.3)', transition:'transform 0.15s, box-shadow 0.2s' }}
              aria-label="Resume your saved notary application"
            >
              Resume Application →
            </button>
            <button className="btn btn-outline btn-sm" onClick={clearDraft} type="button" style={{ fontSize:11, color:'#7A7870' }}>Start Fresh</button>
          </div>
        </div>
      )}

      {/* MAIN CARD */}
      <main id="register-main" tabIndex={-1} style={{ background:'#fff', borderRadius:20, padding:'24px 20px', width:'100%', maxWidth: step===1 ? 560 : 660, marginBottom:32 }}>

        {/* ── STEP 1: ROLE SELECTION ── */}
        {step === 1 && (
          <section aria-labelledby="role-heading">
            <h1 id="role-heading" style={{ fontSize:22, marginBottom:4, color:'#0D1B3E', fontFamily:'Cormorant Garamond, serif', fontWeight:700 }}>Create your account</h1>
            <p style={{ fontSize:13, color:'#7A7870', marginBottom:24 }}>Select how you'll use LCA</p>
            <div role="group" aria-label="Select your account type" style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {ROLES.map(r => (
                <button key={r.id} className="lca-role-card" onClick={() => setRole(r.id)} aria-pressed={role === r.id} type="button">
                  <div style={{ fontWeight:600, fontSize:14, color:'#0D1B3E', marginBottom:2 }}>{r.label}</div>
                  <div style={{ fontSize:12, color:'#7A7870' }}>{r.desc}</div>
                  <div style={{ fontSize:11, color:role===r.id?'#9A7A30':'#B8B5AB', marginTop:4 }}>{r.note}</div>
                </button>
              ))}
            </div>
            <button className="btn btn-navy btn-lg" style={{ width:'100%', marginTop:20 }} disabled={!role} onClick={() => setStep(2)} type="button">Continue →</button>
          </section>
        )}

        {/* ── STEP 2: REGISTRATION FORM ── */}
        {step === 2 && (
          <section aria-labelledby="register-heading">
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:20 }}>
              <button className="lca-back-btn" onClick={() => { setStep(1); setNotaryStep(0) }} type="button">← Back</button>
              <span aria-hidden="true" style={{ fontSize:12, color:'#B8B5AB' }}>|</span>
              <span style={{ fontSize:13, color:'#7A7870' }}>Registering as <strong style={{ color:'#0D1B3E' }}>{ROLES.find(r => r.id === role)?.label}</strong></span>
            </div>
            <h1 id="register-heading" className="sr-only">{ROLES.find(r => r.id === role)?.label} Registration</h1>

            {error && (
              <div className="lca-error" role="alert" aria-live="assertive" aria-atomic="true">
                <span aria-hidden="true">⚠️</span><span>{error}</span>
              </div>
            )}

            {draftRestored && (
              <div className="lca-draft-restored" role="status">
                <span>✓</span>
                <span>Your saved progress has been restored!! Continue from where you left off.</span>
              </div>
            )}

            {/* ── NON-NOTARY FORM ── */}
            {role !== 'notary' && (
              <form onSubmit={handleSubmit} noValidate>
                {/* 🪤 Honeypot — invisible to humans; bots fill it and get quietly discarded */}
                <div aria-hidden="true" style={{ position:'absolute', left:'-9999px', top:'-9999px', height:0, width:0, overflow:'hidden' }}>
                  <label>Company website
                    <input type="text" name="company_website" tabIndex={-1} autoComplete="off" value={honeypot} onChange={e => setHoneypot(e.target.value)} />
                  </label>
                </div>
                <p className="lca-required-note"><span aria-hidden="true">*</span> Required fields</p>
                <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                  {inp('Full legal name', 'full_name', 'text', 'As it appears on ID', true)}
                  {inp('Email address', 'email', 'email', 'you@example.com', true)}
                  {inp('Phone number', 'phone', 'tel', '(904) 555-0100')}
                  <label htmlFor="sms-optin-client" style={{ display:'flex', alignItems:'flex-start', gap:10, fontSize:12.5, color:'#4A4840', lineHeight:1.55, cursor:'pointer', background:'#F8F7F4', border:'1px solid #E8E6DF', borderRadius:10, padding:'12px 14px' }}>
                    <input id="sms-optin-client" type="checkbox" style={{ width:'auto', marginTop:2 }} checked={!!form.sms_opt_in} onChange={e => set('sms_opt_in', e.target.checked)} />
                    <span>📱 <strong>Yes — text me updates.</strong> I agree to receive text messages from Loan Closing Agent, LLC. DBA Nationwide Notary Professionals Network about my bookings and account (e.g., appointment confirmations and status updates). Message frequency varies. Message &amp; data rates may apply. Reply STOP to opt out or HELP for help. Consent is not a condition of service.</span>
                  </label>
                  {role === 'client' && inp('Company name', 'company_name', 'text', 'Sunbelt Title Co.', true)}
                  {role === 'client' && (
                    <>
                      <div className="form-group">
                        <label className="form-label" htmlFor="field-baddr">Business Address</label>
                        <AddressAutocomplete
                          value={form.business_address}
                          onChange={val => set('business_address', val)}
                          onSelect={({ street, city, state, zip }) => {
                            if (street) set('business_address', street)
                            if (city)   set('business_city', city)
                            if (state)  set('business_state', state)
                            if (zip)    set('business_zip', zip)
                          }}
                          placeholder="123 Main Street — start typing to search!!"
                        />
                      </div>
                      <div style={{ display:'grid', gridTemplateColumns:'2fr 60px 80px', gap:8 }}>
                        <div className="form-group">
                          <label className="form-label" htmlFor="field-bcity">City</label>
                          <input id="field-bcity" placeholder="Jacksonville" value={form.business_city} onChange={e => set('business_city', e.target.value)} />
                        </div>
                        <div className="form-group">
                          <label className="form-label" htmlFor="field-bstate">State</label>
                          <input id="field-bstate" placeholder="FL" value={form.business_state} onChange={e => set('business_state', e.target.value)} maxLength={2} />
                        </div>
                        <div className="form-group">
                          <label className="form-label" htmlFor="field-bzip">ZIP</label>
                          <input id="field-bzip" placeholder="32202" value={form.business_zip} onChange={e => { const v = e.target.value.replace(/\D/g,''); set('business_zip', v) }} maxLength={5} inputMode="numeric" pattern="[0-9]*" />
                        </div>
                      </div>
                    </>
                  )}
                  <div className="form-group" style={{ position:'relative' }}>
                    <label className="form-label" htmlFor="field-pw">Password <span style={{ color:'#E24B4A' }} aria-hidden="true"> *</span></label>
                    <input id="field-pw" type={showPassword?'text':'password'} placeholder="Min. 8 characters" value={form.password} onChange={e => set('password', e.target.value)} required style={{ paddingRight:40 }} />
                    <button type="button" onClick={() => setShowPassword(s=>!s)} style={{ position:'absolute', right:12, bottom:10, background:'none', border:'none', cursor:'pointer', color:'#7A7870', fontSize:16 }} aria-label={showPassword?'Hide':'Show'}>{showPassword?'🙈':'👁️'}</button>
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="field-cpw">Confirm password <span style={{ color:'#E24B4A' }} aria-hidden="true"> *</span></label>
                    <input id="field-cpw" type={showPassword?'text':'password'} placeholder="Re-enter password" value={form.confirm_password} onChange={e => set('confirm_password', e.target.value)} required />
                  </div>
                  <button className="btn btn-navy btn-lg" type="submit" disabled={loading} aria-busy={loading}>
                    {loading ? 'Creating account...' : 'Create account'}
                  </button>
                </div>
              </form>
            )}

            {/* ── NOTARY MULTI-STEP FORM ── */}
            {role === 'notary' && (
              <>
                <div style={{ marginBottom:16 }} role="region" aria-label="Registration progress">
                  <div className="lca-progress-label">
                    <span style={{ fontSize:12, fontWeight:500, color:'#0D1B3E' }}>Step {notaryStep+1} of {NOTARY_STEPS.length} — {NOTARY_STEPS[notaryStep]}</span>
                    <span style={{ fontSize:12, color:'#7A7870' }} aria-live="polite">{pct}% complete</span>
                  </div>
                  <div role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={`Registration ${pct}% complete`} style={{ height:6, background:'#E8E6DF', borderRadius:20 }}>
                    <div style={{ height:6, borderRadius:20, background:'#C9A84C', width:`${pct}%`, transition:'width 0.3s' }} />
                  </div>
                  <div style={{ display:'flex', gap:4, marginTop:6 }} aria-hidden="true">
                    {NOTARY_STEPS.map((_, i) => (
                      <div key={i} style={{ flex:1, height:3, borderRadius:2, background:i<=notaryStep?'#C9A84C':'#E8E6DF' }} />
                    ))}
                  </div>
                  <div style={{ fontSize:10, color:'#B8B5AB', textAlign:'right', marginTop:4 }}>
                    💾 Progress auto-saved — you can safely close and resume later
                  </div>
                </div>

                <div className="lca-encouragement" aria-live="polite">
                  <div className="lca-encouragement-emoji">{STEP_ENCOURAGEMENT[notaryStep].emoji}</div>
                  <div>
                    <div className="lca-encouragement-text">{STEP_ENCOURAGEMENT[notaryStep].msg}</div>
                    <div className="lca-encouragement-sub">
                      {notaryStep < 5
                        ? `${NOTARY_STEPS.length - notaryStep - 1} step${NOTARY_STEPS.length - notaryStep - 1 !== 1 ? 's' : ''} remaining to join the LCA Elite Network!!`
                        : 'You made it!! Submit your application below!!'}
                    </div>
                  </div>
                </div>

                <p className="lca-required-note"><span aria-hidden="true">*</span> Required fields</p>

                {/* STEP 0 — PERSONAL INFO */}
                {notaryStep === 0 && (
                  <fieldset style={{ border:'none', padding:0 }}>
                    <legend style={{ fontSize:12, fontWeight:600, color:'#0D1B3E', paddingBottom:8, borderBottom:'1px solid #E8E6DF', marginBottom:14, width:'100%', display:'block' }}>Personal Information</legend>
                    <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                      {inp('Full legal name', 'full_name', 'text', 'Exactly as it appears on government ID', true)}
                      {inp('Email address', 'email', 'email', 'you@example.com', true)}
                      {inp('Phone number', 'phone', 'tel', '(904) 555-0100', true)}
                      <label htmlFor="sms-optin-notary" style={{ display:'flex', alignItems:'flex-start', gap:10, fontSize:12.5, color:'#4A4840', lineHeight:1.55, cursor:'pointer', background:'#F8F7F4', border:'1px solid #E8E6DF', borderRadius:10, padding:'12px 14px' }}>
                        <input id="sms-optin-notary" type="checkbox" style={{ width:'auto', marginTop:2 }} checked={!!form.sms_opt_in} onChange={e => set('sms_opt_in', e.target.checked)} />
                        <span>📱 <strong>Yes — text me updates.</strong> I agree to receive text messages from Loan Closing Agent, LLC. DBA Nationwide Notary Professionals Network about my signing assignments and account (e.g., new assignment alerts, appointment confirmations, and status updates). Message frequency varies. Message &amp; data rates may apply. Reply STOP to opt out or HELP for help. Consent is not a condition of service.</span>
                      </label>
                      <div className="form-group">
                        <label className="form-label" htmlFor="field-physical-address">Physical street address <span aria-hidden="true" style={{ color:'#E24B4A' }}>*</span></label>
                        <AddressAutocomplete
                          value={form.physical_address}
                          onChange={val => set('physical_address', val)}
                          onSelect={({ street, city, state, zip }) => {
                            if (street) set('physical_address', street)
                            if (city)   set('city', city)
                            if (state)  set('state_residence', state)
                            if (zip)  { set('zip', zip); lookupCounties(zip) }
                          }}
                          placeholder="123 Main Street — start typing to search!!"
                          required
                        />
                        <div style={{ fontSize:11, color:'#B8B5AB', marginTop:4 }}>Start typing and select your address from the dropdown — city, state & ZIP will auto-fill!!</div>
                      </div>
                      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(120px, 1fr))', gap:10 }}>
                        <div className="form-group">
                          <label className="form-label" htmlFor="field-city">City <span aria-hidden="true" style={{ color:'#E24B4A' }}>*</span></label>
                          <input id="field-city" placeholder="Jacksonville" value={form.city} onChange={e => set('city', e.target.value)} required />
                        </div>
                        <div className="form-group">
                          <label className="form-label" htmlFor="field-state">State <span aria-hidden="true" style={{ color:'#E24B4A' }}>*</span></label>
                          <select id="field-state" value={form.state_residence} onChange={e => set('state_residence', e.target.value)} required>
                            <option value="">Select...</option>
                            {STATES.map(s => <option key={s}>{s}</option>)}
                          </select>
                        </div>
                        <div className="form-group">
                          <label className="form-label" htmlFor="field-zip">ZIP <span aria-hidden="true" style={{ color:'#E24B4A' }}>*</span></label>
                          <input id="field-zip" placeholder="32202" value={form.zip} onChange={e => { const v = e.target.value.replace(/\D/g,''); set('zip', v); lookupCounties(v) }} required inputMode="numeric" pattern="[0-9]*" maxLength={5} />
                        </div>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Languages spoken <span aria-hidden="true" style={{ color:'#E24B4A' }}>*</span></label>
                        <MultiSelectDropdown label="languages" options={LANGUAGES} selected={form.languages} onChange={val => set('languages', val)} placeholder="Select languages spoken..." />
                      </div>
                      <div className="form-group" style={{ position:'relative' }}>
                        <label className="form-label" htmlFor="field-password">Password <span style={{ color:'#E24B4A' }} aria-hidden="true"> *</span></label>
                        <input id="field-password" type={showPassword?'text':'password'} placeholder="Min. 8 characters" value={form.password} onChange={e => set('password', e.target.value)} required style={{ paddingRight:40 }} />
                        <button type="button" onClick={() => setShowPassword(s=>!s)} style={{ position:'absolute', right:12, bottom:10, background:'none', border:'none', cursor:'pointer', color:'#7A7870', fontSize:16 }} aria-label={showPassword?'Hide password':'Show password'}>{showPassword?'🙈':'👁️'}</button>
                      </div>
                      <div className="form-group">
                        <label className="form-label" htmlFor="field-confirm">Confirm password <span style={{ color:'#E24B4A' }} aria-hidden="true"> *</span></label>
                        <input id="field-confirm" type={showPassword?'text':'password'} placeholder="Re-enter password" value={form.confirm_password} onChange={e => set('confirm_password', e.target.value)} required />
                      </div>
                    </div>
                  </fieldset>
                )}

                {/* STEP 1 — COMMISSION & EXPERIENCE */}
                {notaryStep === 1 && (
                  <fieldset style={{ border:'none', padding:0 }}>
                    <legend style={{ fontSize:12, fontWeight:600, color:'#0D1B3E', paddingBottom:8, borderBottom:'1px solid #E8E6DF', marginBottom:14, width:'100%', display:'block' }}>Commission &amp; Experience</legend>
                    <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                      <div style={{ background:'#EAF5EE', border:'1px solid rgba(45,122,79,0.2)', borderRadius:10, padding:14, marginBottom:4 }}>
                        <div style={{ fontSize:12, fontWeight:600, color:'#2D7A4F', marginBottom:4 }}>🏷️ Your LCA Vendor Number</div>
                        <div style={{ fontSize:12, color:'#4A7A5C', lineHeight:1.6 }}>Your Vendor Number Will Be Assigned by LCA upon Approval!!</div>
                      </div>
                      <div className="form-group">
                        <label className="form-label" htmlFor="field-commission-state">State commissioned in <span aria-hidden="true" style={{ color:'#E24B4A' }}>*</span></label>
                        <select id="field-commission-state" value={form.commission_state} onChange={e => set('commission_state', e.target.value)} required>
                          <option value="">Select state...</option>
                          {STATES.map(s => <option key={s}>{s}</option>)}
                        </select>
                      </div>
                      {inp('Commission number', 'commission_number', 'text', 'FL-XXXXXXX', true)}
                      {inp('Commission expiration date', 'commission_expires', 'date', '', true)}
                      {inp('E&O insurance carrier', 'eo_carrier', 'text', 'National Notary Association', true)}
                      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px, 1fr))', gap:10 }}>
                        <div className="form-group">
                          <label className="form-label" htmlFor="field-eo-coverage">E&amp;O coverage amount</label>
                          <select id="field-eo-coverage" value={form.eo_coverage} onChange={e => set('eo_coverage', e.target.value)}>
                            <option>$25,000</option><option>$50,000</option><option>$100,000</option><option>$250,000</option><option>$500,000</option><option>$1,000,000</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label className="form-label" htmlFor="field-eo-expires">E&amp;O expiration date</label>
                          <input id="field-eo-expires" type="date" value={form.eo_expires} onChange={e => set('eo_expires', e.target.value)} style={{ width:'100%', fontSize:'16px', padding:'8px 10px', border:'1.5px solid #E0D9CC', borderRadius:8 }} />
                        </div>
                      </div>
                      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px, 1fr))', gap:10 }}>
                        {inp('Years of experience', 'years_experience', 'number', '5', true)}
                        {inp('Signings completed', 'signings_completed', 'number', '250', true)}
                      </div>
                      <div className="form-group">
                        <label className="form-label">Types of signings handled <span aria-hidden="true" style={{ color:'#E24B4A' }}>*</span></label>
                        <MultiSelectDropdown label="signing types" options={SIGNING_TYPES} selected={form.signing_types} onChange={val => set('signing_types', val)} placeholder="Select signing types..." />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Notary Signing Agent Certifications</label>
                        <MultiSelectDropdown label="certifications" options={NSA_CERTIFICATIONS} selected={form.nsa_certifications} onChange={val => set('nsa_certifications', val)} placeholder="Select certifications held..." />
                      </div>
                      <div className="form-group">
                        <label className="form-label" style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }}>
                          <input type="checkbox" style={{ width:'auto' }} checked={form.background_check} onChange={e => set('background_check', e.target.checked)} />
                          Background check completed (within 12 months)
                        </label>
                      </div>
                      <div className="form-group">
                        <label className="form-label" style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }}>
                          <input type="checkbox" style={{ width:'auto' }} checked={form.ron_certified} onChange={e => set('ron_certified', e.target.checked)} />
                          Remote Online Notary (RON) certified
                        </label>
                      </div>
                      {form.ron_certified && (
                        <div style={{ background:'#EBF4FF', borderRadius:10, padding:12, display:'flex', flexDirection:'column', gap:8 }}>
                          <div style={{ fontSize:12, fontWeight:600, color:'#185FA5' }}>RON Certification Details</div>
                          {inp('RON platform used', 'ron_platform', 'text', 'e.g. RON Vault, Notarize, Pavaso, DocVerify, SIGNiX')}
                          <div style={{ fontSize:11, color:'#7A7870' }}>You will upload your RON certification proof in the Documents step.</div>
                        </div>
                      )}
                    </div>
                  </fieldset>
                )}

                {/* STEP 2 — COVERAGE & EQUIPMENT */}
                {notaryStep === 2 && (
                  <fieldset style={{ border:'none', padding:0 }}>
                    <legend style={{ fontSize:12, fontWeight:600, color:'#0D1B3E', paddingBottom:8, borderBottom:'1px solid #E8E6DF', marginBottom:14, width:'100%', display:'block' }}>Coverage, Availability &amp; Equipment</legend>
                    <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                      <div className="form-group">
                        <label className="form-label" htmlFor="field-service-areas">Service areas (counties / cities) <span aria-hidden="true" style={{ color:'#E24B4A' }}>*</span></label>
                        <div style={{ position:'relative' }}>
                          <div style={{ display:'flex', gap:8, marginBottom:8 }}>
                            <input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={5} placeholder="Enter ZIP to find counties..." style={{ flex:1, padding:'8px 12px', border:'1.5px solid #E0D9CC', borderRadius:8, fontSize:13 }} onChange={e => { const v = e.target.value.replace(/\D/g,''); if (v.length === 5) lookupCounties(v) }} />
                            <button type="button" onClick={() => { const zip = document.querySelector('#field-service-areas')?.previousSibling?.querySelector('input')?.value || form.zip; if (zip) lookupCounties(zip) }} disabled={countyLooking} style={{ padding:'8px 14px', border:'1.5px solid #C9A84C', borderRadius:8, background:'#FDF6E3', color:'#8A5C00', fontSize:12, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>
                              {countyLooking ? '🔍 Searching...' : '🔍 Find Counties'}
                            </button>
                          </div>
                          <textarea id="field-service-areas" rows={3} placeholder="Counties will appear here automatically — or type manually!!" value={form.service_areas} onChange={e => set('service_areas', e.target.value)} style={{ width:'100%', padding:'10px 14px', border:'1.5px solid #E0D9CC', borderRadius:10, fontSize:13, fontFamily:'inherit', resize:'vertical' }} required />
                          {countyLooking && <div style={{ fontSize:11, color:'#C9A84C', marginTop:4 }}>🔍 Finding counties within 25 miles...</div>}
                        </div>
                        <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:6 }}>
                          {form.service_areas ? <div style={{ fontSize:11, color:'#2D7A4F' }}>✅ Counties loaded!! You can edit or add more manually!!</div> : <div style={{ fontSize:11, color:'#B8B5AB' }}>Enter a ZIP code above to auto-fill nearby counties!!</div>}
                        </div>
                      </div>
                      <div className="form-group">
                        <label className="form-label" htmlFor="field-travel-radius">Maximum travel radius <span aria-hidden="true" style={{ color:'#E24B4A' }}>*</span></label>
                        <select id="field-travel-radius" value={form.travel_radius} onChange={e => set('travel_radius', e.target.value)} required>
                          <option value="10">Up to 10 miles</option>
                          <option value="25">Up to 25 miles</option>
                          <option value="50">Up to 50 miles</option>
                          <option value="75">Up to 75 miles</option>
                          <option value="100">Up to 100 miles</option>
                          <option value="150">Up to 150 miles — statewide</option>
                          <option value="999">Nationwide / No limit</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Availability <span aria-hidden="true" style={{ color:'#E24B4A' }}>*</span></label>
                        <MultiSelectDropdown label="availability" options={['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday','Mornings','Afternoons','Evenings','Last-minute / rush','24-hour notice required']} selected={form.availability} onChange={val => set('availability', val)} placeholder="Select your availability..." />
                      </div>
                      <div style={{ background:'#F8F7F4', borderRadius:12, padding:16, border:'1px solid #E8E6DF' }}>
                        <div style={{ fontSize:12, fontWeight:600, color:'#0D1B3E', marginBottom:12 }}>🖨️ Equipment — Required for loan signings</div>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                          <div className="form-group">
                            <label className="form-label" htmlFor="field-printer">Printer type <span aria-hidden="true" style={{ color:'#E24B4A' }}>*</span></label>
                            <select id="field-printer" value={form.printer_type} onChange={e => set('printer_type', e.target.value)} required>
                              <option value="">Select printer...</option>
                              <option value="laser">Laser Printer</option>
                              <option value="dual_tray_laser">Dual Tray Laser Printer</option>
                              <option value="inkjet">Inkjet Printer</option>
                              <option value="dual_tray_inkjet">Dual Tray Inkjet Printer</option>
                              <option value="other">Other</option>
                            </select>
                            {form.printer_type === 'other' && <input style={{ marginTop:8, width:'100%', padding:'8px 12px', border:'1.5px solid #E0D9CC', borderRadius:8, fontSize:13 }} placeholder="Please describe your printer..." value={form.printer_type_other || ''} onChange={e => set('printer_type_other', e.target.value)} />}
                          </div>
                          <div className="form-group">
                            <label className="form-label" htmlFor="field-scanner">Scanner type <span aria-hidden="true" style={{ color:'#E24B4A' }}>*</span></label>
                            <select id="field-scanner" value={form.scanner_type} onChange={e => set('scanner_type', e.target.value)} required>
                              <option value="">Select scanner...</option>
                              <option value="mobile_app">Mobile Scan App</option>
                              <option value="flatbed">Flatbed Scanner</option>
                              <option value="portable">Portable Scanner</option>
                              <option value="adf">ADF Scanner</option>
                              <option value="none">No scanner</option>
                            </select>
                          </div>
                        </div>
                        <div style={{ fontSize:11, color:'#7A7870', marginTop:8 }}>Dual tray printers are preferred for loan signings. Mobile scan apps are accepted for scanbacks.</div>
                      </div>
                    </div>
                  </fieldset>
                )}

                {/* STEP 3 — PAYMENT */}
                {notaryStep === 3 && (
                  <fieldset style={{ border:'none', padding:0 }}>
                    <legend style={{ fontSize:12, fontWeight:600, color:'#0D1B3E', paddingBottom:8, borderBottom:'1px solid #E8E6DF', marginBottom:14, width:'100%', display:'block' }}>Payment Information</legend>
                    <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                      <div className="form-group">
                        <label className="form-label" htmlFor="field-payment-method">Preferred payment method <span aria-hidden="true" style={{ color:'#E24B4A' }}>*</span></label>
                        <select id="field-payment-method" value={form.payment_method} onChange={e => set('payment_method', e.target.value)}>
                          <option>Direct Deposit</option>
                          <option>Check</option>
                        </select>
                      </div>
                      {form.payment_method === 'Direct Deposit' && (
                        <>
                          <div className="alert alert-info" style={{ fontSize:12 }} role="note">Your banking details are encrypted and used only for direct deposit payments.</div>
                          {inp('Bank name', 'bank_name', 'text', 'Wells Fargo', true)}
                          {inp('Routing number', 'routing_number', 'text', '9-digit ABA routing number', true)}
                          {inp('Account number', 'account_number', 'text', 'Checking account number', true)}
                        </>
                      )}
                      {form.payment_method === 'Check' && (
                        <>
                          <label className="lca-checkbox-label">
                            <input type="checkbox" style={{ width:'auto' }} checked={form.mailing_address_same} onChange={e => set('mailing_address_same', e.target.checked)} />
                            Mailing address same as physical address
                          </label>
                          {!form.mailing_address_same && inp('Check mailing address', 'check_mailing_address', 'text', 'Full address for check delivery', true)}
                          <div className="alert alert-warning" style={{ fontSize:12 }} role="note">Checks are mailed within 5 business days of job completion.</div>
                        </>
                      )}
                      <div style={{ background:'#F8F7F4', borderRadius:10, padding:14 }} role="note">
                        <div style={{ fontSize:12, fontWeight:600, color:'#0D1B3E', marginBottom:4 }}>W-9 Requirement</div>
                        <div style={{ fontSize:12, color:'#7A7870', lineHeight:1.6 }}>As an independent contractor, a completed W-9 is required. You will upload it in the next step. A 1099 will be issued for annual earnings over $600.</div>
                      </div>
                    </div>
                  </fieldset>
                )}

                {/* STEP 4 — DOCUMENTS */}
                {notaryStep === 4 && (
                  <fieldset style={{ border:'none', padding:0 }}>
                    <legend style={{ fontSize:12, fontWeight:600, color:'#0D1B3E', paddingBottom:8, borderBottom:'1px solid #E8E6DF', marginBottom:20, width:'100%', display:'block' }}>Documents &amp; Credentials</legend>
                    <div style={{ textAlign:'center', padding:'20px 0' }}>
                      <div style={{ fontSize:48, marginBottom:16 }}>🎉</div>
                      <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:24, fontWeight:600, color:'#0D1B3E', marginBottom:8 }}>Almost there!!</div>
                      <div style={{ fontSize:14, color:'#7A7870', lineHeight:1.8, maxWidth:420, margin:'0 auto 24px' }}>
                        Once your application is reviewed and approved, you'll receive a welcome email with instructions to upload your credentials directly in your notary portal!!
                      </div>
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                      {[
                        { icon:'🪪', label:'Government-issued Photo ID (Front & Back)' },
                        { icon:'📜', label:'Notary Commission Certificate' },
                        { icon:'🛡️', label:'E&O Insurance Declaration Page' },
                        { icon:'📋', label:'W-9 Form (completed & signed)' },
                        { icon:'🔍', label:'Background Check (within 12 months)' },
                      ].map((doc, i) => (
                        <div key={i} style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 16px', background:'#F8F7F4', borderRadius:10, border:'1px solid #E8E6DF' }}>
                          <span style={{ fontSize:22 }}>{doc.icon}</span>
                          <span style={{ fontSize:13, color:'#4A4840' }}>{doc.label}</span>
                          <span style={{ marginLeft:'auto', fontSize:11, color:'#B8B5AB' }}>Upload after approval</span>
                        </div>
                      ))}
                    </div>
                    <div style={{ background:'#EBF4FF', border:'1px solid rgba(59,130,246,0.2)', borderRadius:12, padding:16, marginTop:20, fontSize:12, color:'#1A4A6B', lineHeight:1.7 }}>
                      💡 <strong>Good to know:</strong> You'll upload these documents securely in your notary portal after your account is approved. Our team reviews all credentials within 1 business day!!
                    </div>
                  </fieldset>
                )}

                {/* STEP 5 — REVIEW & SIGN */}
                {notaryStep === 5 && (
                  <form onSubmit={handleSubmit} noValidate aria-label="Notary application review and signature">
                  {/* 🪤 Honeypot — invisible to humans; bots fill it and get quietly discarded */}
                  <div aria-hidden="true" style={{ position:'absolute', left:'-9999px', top:'-9999px', height:0, width:0, overflow:'hidden' }}>
                    <label>Company website
                      <input type="text" name="company_website" tabIndex={-1} autoComplete="off" value={honeypot} onChange={e => setHoneypot(e.target.value)} />
                    </label>
                  </div>
                    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
                      <div style={{ fontSize:12, fontWeight:600, color:'#0D1B3E', paddingBottom:8, borderBottom:'1px solid #E8E6DF' }}>Review &amp; Sign</div>
                      <div style={{ background:'#F8F7F4', borderRadius:10, padding:16 }} role="region" aria-label="Application summary">
                        <div style={{ fontSize:12, fontWeight:600, color:'#0D1B3E', marginBottom:10 }}>Application Summary</div>
                        <dl style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6, fontSize:12, color:'#4A4840' }}>
                          {[
                            ['Name', form.full_name], ['Email', form.email], ['Phone', form.phone],
                            ['Commission state', form.commission_state],
                            ['Commission #', form.commission_number], ['Experience', form.years_experience + ' years'],
                            ['Signings done', form.signings_completed], ['Travel radius', form.travel_radius + ' miles'],
                            ['Payment', form.payment_method], ['Languages', form.languages.join(', ')],
                            ['Printer', form.printer_type ? form.printer_type.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase()) : '—'],
                            ['Scanner', form.scanner_type ? form.scanner_type.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase()) : '—'],
                            ['RON Certified', form.ron_certified ? 'Yes — ' + (form.ron_platform || 'platform TBD') : 'No'],
                            ['Documents uploaded', Object.keys(uploadedFiles).length + ' files'],
                            ['Vendor #', '🏷️ Will be assigned by LCA upon approval'],
                          ].map(([k, v]) => (
                            <div key={k}>
                              <dt style={{ color:'#7A7870', display:'inline' }}>{k}: </dt>
                              <dd style={{ display:'inline' }}>{v || '—'}</dd>
                            </div>
                          ))}
                        </dl>
                      </div>

                      {/* LEGAL AGREEMENTS */}
                      <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                        <div style={{ background:'#0D1B3E', borderRadius:10, padding:16 }}>
                          <div style={{ fontSize:11, color:'#C9A84C', fontWeight:600, marginBottom:8, letterSpacing:'0.06em' }}>📋 INDEPENDENT CONTRACTOR AGREEMENT (ICA)</div>
                          <div style={{ fontSize:11, color:'rgba(255,255,255,0.7)', lineHeight:1.8, maxHeight:120, overflowY:'auto', marginBottom:10, padding:'8px', background:'rgba(255,255,255,0.05)', borderRadius:6 }}>
                            I understand and agree that: (1) I am an independent contractor of Loan Closing Agent, LLC ("LCA") and NOT an employee. (2) I am solely responsible for all federal, state, and local taxes on compensation received, including self-employment taxes. LCA will issue a 1099 for annual earnings over $600. (3) This agreement is non-exclusive — I retain the right to work for other signing services and direct clients. (4) I provide my own tools including printer, paper, vehicle, and mobile device. (5) I decide which assignments to accept or decline. (6) I must maintain an active notary commission, current E&O insurance (minimum $25,000), and keep all credentials current. (7) I agree to adhere to the Notary Signing Agent Code of Conduct, maintain professional neutrality, and not provide legal advice to any party. (8) I agree to indemnify and hold harmless Loan Closing Agent, LLC from any claims, losses, or damages arising from my negligence or errors during a signing. (9) I consent to background screening as required by LCA or its clients. (10) Fees are agreed upon at the time of job acceptance. Payment is processed weekly via direct deposit for completed assignments.
                          </div>
                          <label style={{ display:'flex', alignItems:'flex-start', gap:10, cursor:'pointer' }}>
                            <input type="checkbox" style={{ width:'auto', marginTop:2, flexShrink:0 }} checked={form.agreed_ica || false} onChange={e => set('agreed_ica', e.target.checked)} aria-required="true" />
                            <span style={{ fontSize:12, color:'#C9A84C', fontWeight:500 }}>I have read and agree to the Independent Contractor Agreement</span>
                          </label>
                        </div>

                        <div style={{ background:'#0D1B3E', borderRadius:10, padding:16 }}>
                          <div style={{ fontSize:11, color:'#C9A84C', fontWeight:600, marginBottom:8, letterSpacing:'0.06em' }}>🔒 CONFIDENTIALITY & DATA PROTECTION (NPPI)</div>
                          <div style={{ fontSize:11, color:'rgba(255,255,255,0.7)', lineHeight:1.8, maxHeight:120, overflowY:'auto', marginBottom:10, padding:'8px', background:'rgba(255,255,255,0.05)', borderRadius:6 }}>
                            I acknowledge that in performing signing services I will have access to Non-Public Personal Information ("NPPI") including but not limited to borrower names, Social Security numbers, loan amounts, property addresses, and financial information. I agree to: (1) Use secure networks only when downloading or transmitting loan documents. (2) Never share borrower information, loan terms, or transaction details with any third party. (3) Immediately shred or destroy all misprints, extra copies, or documents containing NPPI. (4) Never leave documents unattended in public places or unsecured vehicles. (5) Report any suspected data breach or unauthorized access to LCA immediately. (6) Comply with all applicable privacy laws including the Gramm-Leach-Bliley Act (GLBA). Violation of this agreement may result in immediate termination and potential legal action.
                          </div>
                          <label style={{ display:'flex', alignItems:'flex-start', gap:10, cursor:'pointer' }}>
                            <input type="checkbox" style={{ width:'auto', marginTop:2, flexShrink:0 }} checked={form.agreed_nppi || false} onChange={e => set('agreed_nppi', e.target.checked)} aria-required="true" />
                            <span style={{ fontSize:12, color:'#C9A84C', fontWeight:500 }}>I have read and agree to the Confidentiality & Data Protection Agreement</span>
                          </label>
                        </div>

                        <div style={{ background:'#0D1B3E', borderRadius:10, padding:16 }}>
                          <div style={{ fontSize:11, color:'#C9A84C', fontWeight:600, marginBottom:8, letterSpacing:'0.06em' }}>🤝 NON-SOLICITATION AGREEMENT</div>
                          <div style={{ fontSize:11, color:'rgba(255,255,255,0.7)', lineHeight:1.8, maxHeight:100, overflowY:'auto', marginBottom:10, padding:'8px', background:'rgba(255,255,255,0.05)', borderRadius:6 }}>
                            During the term of this agreement and for a period of twelve (12) months following termination, I agree not to directly solicit or accept direct business from any title company, escrow officer, lender, or client that was introduced to me through Loan Closing Agent, LLC assignments, for the purpose of bypassing LCA as the intermediary. This clause does not restrict me from working with clients I had prior to joining LCA or clients I obtained through my own independent marketing efforts unrelated to LCA assignments.
                          </div>
                          <label style={{ display:'flex', alignItems:'flex-start', gap:10, cursor:'pointer' }}>
                            <input type="checkbox" style={{ width:'auto', marginTop:2, flexShrink:0 }} checked={form.agreed_nonsolicitation || false} onChange={e => set('agreed_nonsolicitation', e.target.checked)} aria-required="true" />
                            <span style={{ fontSize:12, color:'#C9A84C', fontWeight:500 }}>I have read and agree to the Non-Solicitation Agreement</span>
                          </label>
                        </div>

                        <div style={{ background:'#0D1B3E', borderRadius:10, padding:16 }}>
                          <div style={{ fontSize:11, color:'#C9A84C', fontWeight:600, marginBottom:8, letterSpacing:'0.06em' }}>✍️ CERTIFICATION STATEMENT</div>
                          <p style={{ fontSize:12, color:'rgba(255,255,255,0.85)', lineHeight:1.8 }}>
                            I certify under penalty of perjury that all information provided in this application is true, accurate, and complete to the best of my knowledge. I understand that any false statements or omissions may result in immediate termination of my account and potential legal action. I agree to notify Loan Closing Agent LLC of any changes to my commission status, insurance coverage, or contact information within 5 business days.
                          </p>
                          <label className="lca-checkbox-label" style={{ marginTop:14, alignItems:'flex-start' }}>
                            <input type="checkbox" style={{ width:'auto', marginTop:2, flexShrink:0 }} checked={certChecked} onChange={e => setCertChecked(e.target.checked)} aria-required="true" />
                            <span style={{ fontSize:12, color:'#C9A84C', fontWeight:500 }}>I certify that all information is true and accurate</span>
                          </label>
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label" htmlFor="digital-signature" style={{ fontSize:13, fontWeight:600, color:'#0D1B3E' }}>
                          Digital Signature <span aria-hidden="true" style={{ color:'#E24B4A' }}>*</span>
                        </label>
                        <p style={{ fontSize:12, color:'#7A7870', marginBottom:6 }} id="sig-instructions">
                          Type your full legal name exactly as entered to serve as your digital signature
                        </p>
                        <input
                          id="digital-signature"
                          placeholder={form.full_name || 'Your full legal name'}
                          value={digitalSignature}
                          onChange={e => setDigitalSignature(e.target.value)}
                          aria-describedby="sig-instructions sig-status"
                          aria-required="true"
                          style={{ fontFamily:'Cormorant Garamond, serif', fontSize:18, fontStyle:'italic', borderColor: digitalSignature && digitalSignature.toLowerCase().trim() === form.full_name.toLowerCase().trim() ? '#2D7A4F' : '#E8E6DF' }}
                        />
                        {digitalSignature && (
                          <div style={{ background:'#F8F7F4', border:'1px solid #E8E6DF', borderRadius:8, padding:'8px 12px', marginTop:6 }}>
                            <div style={{ fontSize:11, color:'#0D1B3E', fontWeight:600, marginBottom:2 }}>📅 Signature Timestamp</div>
                            <div style={{ fontSize:11, color:'#4A4840' }}>🕐 {new Date().toLocaleDateString('en-US', { weekday:'long', year:'numeric', month:'long', day:'numeric' })} at {new Date().toLocaleTimeString('en-US', { hour:'2-digit', minute:'2-digit', second:'2-digit', timeZoneName:'short' })}</div>
                            <div style={{ fontSize:10, color:'#B8B5AB', marginTop:2 }}>IP timestamp recorded for compliance purposes</div>
                          </div>
                        )}
                        <div id="sig-status" aria-live="polite">
                          {digitalSignature && digitalSignature.toLowerCase().trim() === form.full_name.toLowerCase().trim() && <p className="lca-sig-ok">✓ Signature verified</p>}
                          {digitalSignature && digitalSignature.toLowerCase().trim() !== form.full_name.toLowerCase().trim() && <p className="lca-sig-err">Must match: "{form.full_name}"</p>}
                        </div>
                        <div style={{ fontSize:11, color:'#B8B5AB', marginTop:6, display:'flex', flexDirection:'column', gap:2 }}>
                          <span>🕐 Signed electronically on {new Date().toLocaleDateString('en-US', { weekday:'long', year:'numeric', month:'long', day:'numeric' })} at {new Date().toLocaleTimeString('en-US', { hour:'2-digit', minute:'2-digit', second:'2-digit', timeZoneName:'short' })}</span>
                          <span style={{ fontSize:10, color:'#C8C5BE' }}>IP timestamp recorded for compliance purposes</span>
                        </div>
                      </div>

                      <button className="btn btn-navy btn-lg" type="submit" disabled={loading || !certChecked || !form.agreed_ica || !form.agreed_nppi || !form.agreed_nonsolicitation} aria-busy={loading}>
                        {loading ? 'Submitting application...' : 'Submit Application →'}
                      </button>
                    </div>
                  </form>
                )}

                {/* PREV / NEXT BUTTONS */}
                {notaryStep < 5 && (
                  <div className="lca-nav-btns">
                    {notaryStep > 0 && (
                      <button className="btn btn-outline" style={{ flex:1 }} onClick={() => setNotaryStep(s => s-1)} type="button">← Previous</button>
                    )}
                    <button className="btn btn-navy" style={{ flex:2 }} onClick={() => {
                      if (notaryStep === 0) {
                        if (!form.full_name) { toast.error('Please enter your full legal name!!'); return }
                        if (!form.email)     { toast.error('Please enter your email address!!'); return }
                        if (!form.phone)     { toast.error('Please enter your phone number!!'); return }
                        if (!form.zip)       { toast.error('Please enter your ZIP code!!'); return }
                      }
                      if (notaryStep === 1) {
                        if (!form.commission_state) { toast.error('⚠️ Please select your commission state — this is required!!', { duration: 5000 }); return }
                        if (!form.commission_number) { toast.error('Please enter your commission number!!'); return }
                        if (!form.commission_expires) { toast.error('Please enter your commission expiration date!!'); return }
                      }
                      if (notaryStep === 2) {
                        if (!form.service_areas) { toast.error('Please enter your service areas!!'); return }
                        if (form.availability.length === 0) { toast.error('Please select your availability!!'); return }
                      }
                      const nextStep = notaryStep + 1
                      setNotaryStep(nextStep)
                      if (nextStep === 2 && form.zip && !form.service_areas) {
                        setTimeout(() => lookupCounties(form.zip), 500)
                      }
                    }} type="button">
                      {notaryStep === 4 ? 'Review & Sign →' : 'Next →'}
                    </button>
                  </div>
                )}
              </>
            )}

            <div style={{ marginTop:20, textAlign:'center', fontSize:13, color:'#7A7870' }}>
              Already have an account?{' '}
              <Link to="/login" style={{ color:'#9A7A30', fontWeight:600 }}>Sign in</Link>
            </div>
          </section>
        )}
      </main>
    </div>
  )
}
